import { useCallback, useEffect } from "react";
import { useBlocker, type BlockerFunction } from "react-router-dom";
import { useFormFlowStore } from "./formFlowStore";
import { hasFilledFormFlowValue } from "./utils";

export type FormFlowNavigationBlockerAPI = {
  proceed: () => void;
  cancel: () => void;
};

export const useFormFlowUnsavedNavigationGuard = (
  instanceId: string,
  onBlock: (api: FormFlowNavigationBlockerAPI) => void,
) => {
  const runtimeState = useFormFlowStore((state) => state.flows[instanceId]);
  const shouldBlockNavigation =
    Boolean(runtimeState?.isDirty) &&
    hasFilledFormFlowValue(runtimeState?.formState) &&
    runtimeState?.submitStatus !== "submitting" &&
    runtimeState?.submitStatus !== "success";

  const shouldBlock: BlockerFunction = useCallback(
    ({ currentLocation, nextLocation }) =>
      shouldBlockNavigation &&
      currentLocation.pathname !== nextLocation.pathname,
    [shouldBlockNavigation],
  );

  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    return () => {
      if (blocker.state === "blocked") {
        blocker.reset();
      }
    };
  }, [blocker]);

  useEffect(() => {
    if (blocker.state !== "blocked") {
      return;
    }

    onBlock({
      proceed: blocker.proceed,
      cancel: blocker.reset,
    });
  }, [blocker, onBlock]);
};
